import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { getBackendUrl } from '../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/**
 * Hook para gerenciar o status da integração WhatsApp API
 * Retorna o status e função para enviar mensagens
 */
export const useWhatsAppIntegration = () => {
  const [status, setStatus] = useState('disconnected'); // 'connected' | 'disconnected' | 'error'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar status da integração
  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/whatsapp/status`);
      
      if (response.data.connected === true) {
        setStatus('connected');
        setError(null);
      } else {
        setStatus('disconnected');
      }
    } catch (err) {
      console.error('Error loading WhatsApp status:', err);
      setStatus('error');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Enviar mensagem via API (quando conectado)
  const sendMessage = useCallback(async (phone, message) => {
    if (status !== 'connected') {
      throw new Error('WhatsApp API não está conectada. Use o fallback manual.');
    }

    try {
      const response = await axios.post(`${API}/whatsapp/send-message`, {
        phone: phone,
        message: message
      });

      return {
        success: true,
        data: response.data
      };
    } catch (err) {
      console.error('Error sending WhatsApp message:', err);
      return {
        success: false,
        error: err.response?.data?.detail || err.message
      };
    }
  }, [status]);

  // Carregar status ao montar
  useEffect(() => {
    loadStatus();
    
    // Recarregar a cada 30 segundos
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  return {
    status,           // 'connected' | 'disconnected' | 'error'
    loading,
    error,
    isConnected: status === 'connected',
    sendMessage,      // Função para enviar via API
    refreshStatus: loadStatus
  };
};

/**
 * Função auxiliar para gerar link wa.me (fallback)
 */
export const generateWhatsAppLink = (phone, message) => {
  const cleanPhone = phone.replace(/\D/g, '');
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
};
